/* eslint-disable max-nested-callbacks */
/* eslint-disable max-statements */
import { unescapeHTML } from '@ds/swymkit/utils'
import { defineStore } from 'pinia'

import { SearchService } from '~/entities/user/user/api/search'
import type { IUser } from '~/entities/user/user/interfaces/user'
import { useUsersStore } from '~/entities/user/user/stores/users'

import { AI_NAME, AI_USER, AI_USER_FOR_IS_TYPING } from '~/features-swym/aura/config/ai'
import type { IComment } from '~/features-swym/comment/interfaces/comment'

import type { COMMENT_ID, MESSAGE_ID, THREAD_ID } from '~/features-shared/content/stores/contents'
import { useContentsStore } from '~/features-shared/content/stores/contents'
import { contentsMap } from '~/features-shared/content/stores/contents-db'
import { useCommentsStoreV2 } from '~/features-shared/content/stores/v2-comments'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { RTInterfaceAPI } from '~/features-shared/rtconv/api/rtinterface-api'
import * as RTConv from '~/features-shared/rtconv/api/rtinterface-senders'
import type { RTCInternalEventType, RTCTextContent } from '~/features-shared/rtconv/interfaces/rtconversation'
import type {
  IMessageDetails,
  IMessageDetailsInternalEvent,
  ITimeline,
} from '~/features-shared/timeline/interfaces/timeline'
import { TimelineStatus } from '~/features-shared/timeline/interfaces/timeline'
import {
  isMessageDetails,
  isMessageDetailsInternalEvent,
  isMessageDetailsWithOnlySubjectUri,
} from '~/features-shared/timeline/utils/is-message-details'
import { getTemporaryTimelineMessage } from '~/features-shared/timeline/utils/temporary-message'

import type { WithRequired } from '~/features-generic/libs/interfaces/utils'

export const useContentsStoreV2 = defineStore('contents-v2', () => {
  const storeUsers = useUsersStore()
  const storeContents = useContentsStore()
  const storeComments = useCommentsStoreV2()

  // #region STATE
  const messagesMap: Map<MESSAGE_ID, ITimeline> = reactive(new Map())
  const comments_id: Map<COMMENT_ID, MESSAGE_ID> = reactive(new Map())
  const thread_timelines_id: Map<THREAD_ID, MESSAGE_ID[]> = reactive(new Map())
  const filtered_thread_timelines_id: Map<THREAD_ID, MESSAGE_ID[]> = reactive(new Map())
  // #endregion

  // #region COMPUTED
  const currentQuery = ref('')

  const page = ref(0) // for pagination of filter on contents

  const currentTimelineId = computed<string>(() => storeContents.thread_selected.split(':')
    .pop() || storeContents.thread_selected)

  const currentTimelineSubjectUri = computed<SUBJECT_6W_URI>(() => storeContents.thread_selected)

  const currentTimeline = computed<string[]>(
    () => (!currentQuery.value
      ? thread_timelines_id.get(currentTimelineId.value)
      : filtered_thread_timelines_id.get(currentTimelineId.value)) || [],
  )

  const currentTimelineList = computed<ITimeline[]>(
    () => currentTimeline.value
      .map(id => messagesMap.get(id)!)
      .filter(el => el),
  )

  // #endregion

  // #region METHODS
  const getTimelineById = (thread_id: THREAD_ID) => thread_timelines_id.get(thread_id) || []

  const getFilteredTimelineById = (thread_id: THREAD_ID) => filtered_thread_timelines_id.get(thread_id) || []

  const getTimelineListById = (thread_id: THREAD_ID) => getTimelineById(thread_id)
    .map(id => messagesMap.get(id))
    .filter(el => el) as ITimeline[]

  const getMessageById = (message_id: MESSAGE_ID): ITimeline | undefined => messagesMap.get(message_id)

  const deleteMessage = (messageId: string, threadId: string): void => {
    const timeline = getTimelineById(threadId)

    const messageIndex = timeline.findIndex(el => el === messageId)

    if (messageIndex !== -1)
      timeline.splice(messageIndex, 1)

    messagesMap.delete(messageId)
  }

  /**
   * Interface to edit a text message.
   */
  interface EditMessageTextOptions {
    /** Id of the message. */
    message_id: string
    /** Text message. */
    text: string
    /** Date of the message edition. */
    edition_date?: string
    /** If the message was created by the ai. */
    isBot?: boolean
  }

  /**
   * Edit a text message in the timeline map.
   * @param options - Options.
   * @param options.message_id Id of the message.
   * @param options.text Text message.
   * @param options.edition_date Date of the message edition.
   * @param options.isBot Is an AI message?
   */
  async function editMessageText(options: EditMessageTextOptions): Promise<void> {
    const message = messagesMap.get(options.message_id)
    const messageDetails = message?.message_details

    if (!message
      || message.message_type !== 'INSTANT_MESSAGE'
      || !messageDetails
      || !isMessageDetails(messageDetails)
    ) {
      return
    }

    if (options.isBot) {
      messageDetails.content = options.text
    }
    else if (options.edition_date) {
      messageDetails.content = options.text
      messageDetails.moderation_state = 'moderatedByAuthor'
      message.updated_at = options.edition_date
      message.modification_date = options.edition_date
    }
  }

  const resetFilter = () => {
    filtered_thread_timelines_id.set(currentTimelineId.value, [])
    currentQuery.value = ''
    page.value = 0
  }
  // #endregion

  // #region ACTIONS
  const _fetchTimelineWithFilters = async (
    refine?: string[],
    query = '',
    oldestMessageDate?: Date,
  ) => {
    const convId = Number(currentTimelineId.value)

    // Text messages
    const textMessages = RTConv.getMessages({
      convId,
      text: query,
      before: oldestMessageDate,
      waitForResponse: true,
    })

    // Rich contents
    const searchResults = await SearchService.getContentsFromFedSearch(
      currentTimelineSubjectUri.value,
      query,
      page.value,
    )
    const contentsUris = searchResults?.map(result =>
      result?.attributes[0]?.value && btoa(unescape(encodeURIComponent(result.attributes[0].value))))

    if (contentsUris?.length > 0) {
      const richContents = RTConv.getMessages({
        convId,
        contents: contentsUris,
        text: query,
        waitForResponse: true,
      })
      page.value++

      const promiseResults = await Promise.allSettled([textMessages, richContents])
      return promiseResults.reduce((timelines, result) => {
        if (result.status === 'fulfilled' && Array.isArray(result.value) && result.value.length > 0)
          timelines.push(...result.value)
        return timelines
      }, [] as Array<ITimeline>)
    }
    else {
      return textMessages
    }
  }

  // todo refine
  const fetchTimeline = async (
    refine?: string[],
    query = '',
    lastMessageId?: string,
  ): Promise<ITimeline[]> => {
    const timeline: MESSAGE_ID[] = query && query === currentQuery.value
      ? getFilteredTimelineById(currentTimelineId.value)
      : getTimelineById(currentTimelineId.value)

    if (timeline?.length && !query && lastMessageId === currentTimelineList.value[0]?.id) {
      resetFilter()

      currentTimelineList.value.forEach((message: ITimeline) => {
        const messageDetails = message?.message_details

        if (!messageDetails || !isMessageDetailsWithOnlySubjectUri(messageDetails))
          return

        const subjectUri = messageDetails.subject_uri

        if (subjectUri && !contentsMap.has(subjectUri)) {
          storeContents.fetchContent(subjectUri, true)
        }
      })

      return currentTimelineList.value
    }

    const convId = Number(currentTimelineId.value)

    if (!Number.isNaN(convId)) {
      resetFilter()

      if (query) {
        currentQuery.value = query
        return _fetchTimelineWithFilters(refine, query)
      }
      else {
        return RTConv.getMessages({
          convId,
          waitForResponse: true,
        })
      }
    }

    return []
  }

  const refreshTimeline = async (refine: string[] = [], query = '') => {
    thread_timelines_id.set(currentTimelineId.value, [])
    return fetchTimeline(refine, query)
  }

  // todo fetch specific page ()= fetch around), refine
  const fetchNextPage = (refine?: string[], query = '') => {
    if (!currentTimelineList.value?.length || query !== currentQuery.value)
      return fetchTimeline(refine, query)

    const oldestMessage = currentTimelineList.value[currentTimelineList.value.length - 1]
    if (!oldestMessage || !oldestMessage.created_at)
      return []

    const convId = Number(currentTimelineId.value)
    const oldestMessageDate = new Date(oldestMessage.created_at)
    if (!Number.isNaN(convId)) {
      if (query) {
        return _fetchTimelineWithFilters(
          refine,
          query,
          oldestMessageDate,
        )
      }
      else {
        return RTConv.getMessages({
          convId,
          before: oldestMessageDate,
          waitForResponse: true,
        })
      }
    }
    return []
  }

  const fetchTimelineAroundMessage = async (
    refine: string[] = [],
    query = '',
    from = '',
  ) => {
    const msgId = Number(from)
    const convId = Number(currentTimelineId.value)
    if (!Number.isNaN(convId) && !Number.isNaN(msgId)) {
      return RTConv.getMessages({
        convId,
        around: msgId,
        waitForResponse: true,
      })
    }
    return []
  }

  const mergeMessage = (message: Partial<ITimeline>): void => {
    if (!message.message_uri)
      return

    const c = messagesMap.get(message.message_uri)
    messagesMap.set(message.message_uri, { ...c, ...message } as ITimeline)

    const messageDetails = message.message_details

    if (messageDetails
      && isMessageDetailsInternalEvent(messageDetails)
      && messageDetails.model === 'Comment'
    ) {
      comments_id.set(messageDetails.model_uri, message.message_uri)
    }
  }

  // eslint-disable-next-line complexity
  const addMessage = (
    message: ITimeline,
    thread_id?: string,
    query = '',
  ): void => {
    mergeMessage(message)

    if (!thread_id)
      thread_id = currentTimelineId.value

    if (query !== currentQuery.value)
      return

    if (!thread_timelines_id.has(thread_id))
      thread_timelines_id.set(thread_id, [])

    if (!filtered_thread_timelines_id.has(thread_id) && currentQuery.value)
      filtered_thread_timelines_id.set(thread_id, [])

    const timeline: MESSAGE_ID[] = (!currentQuery.value)
      ? getTimelineById(thread_id)
      : getFilteredTimelineById(thread_id)

    if (!timeline.includes(message.message_uri)) {
      const timelineList = getTimelineListById(thread_id)
      const firstOldestIndex = timelineList.findIndex(el => el.created_at < message.created_at)

      switch (firstOldestIndex) {
        case -1:
          timeline.push(message.message_uri)
          break
        case 0:
          timeline.unshift(message.message_uri)
          break
        default:
          timeline.splice(
            firstOldestIndex,
            0,
            message.message_uri,
          )
          break
      }
    }

    if ((message.author.login === storeUsers.currentUser?.login || message.author.login === AI_USER_FOR_IS_TYPING)
      && message.triggered_by !== 'TMP_MESSAGE'
    ) {
      // try to find equivalent message
      const index = timeline.findIndex((el) => {
        const content = messagesMap.get(el)

        if (content?.triggered_by === 'TMP_MESSAGE' && content.message_uri === message['X-Request-ID'])
          return true

        return false
      })

      // TMP_MESSAGE found, remove it
      if (index >= 0)
        deleteMessage(timeline[index], thread_id)
    }
  }

  const removeMessage = async (
    message: WithRequired<Partial<ITimeline>, 'id'>,
    threadId?: string,
  ): Promise<void> => {
    if (!message.id)
      return

    if (!threadId)
      threadId = currentTimelineId.value

    await RTConv.deleteMessage({
      convId: Number(threadId),
      msgId: Number(message.id),
      waitForResponse: false,
    })
  }

  const editMessage = async (message: Partial<ITimeline>, text: string) => {
    if (message.id && message.message_type === 'INSTANT_MESSAGE') {
      RTConv.editMessage({
        convId: Number(currentTimelineId.value),
        msgId: Number(message.id),
        text,
        waitForResponse: false,
      })
    }
  }

  /**
   * Add a temporary AI answer to the timeline.
   * @param temp_message_id Temporary ID associated to the current AI answer.
   * @param parent_comment Related parent message - optional
   */
  async function addTemporaryAIMessage(
    conversation_id: string,
    temp_message_id: string,
    parent_comment?: ITimeline,
  ): Promise<void> {
    const item = await getTemporaryTimelineMessage({
      external_app_id: 'RTC',
      currentTimelineId: conversation_id,
      message_uri: temp_message_id,
      author: {
        uid: AI_USER_FOR_IS_TYPING,
        id: '12789',
        first_name: AI_NAME,
        last_name: '',
        login: AI_USER_FOR_IS_TYPING,
        displayName: AI_NAME,
        tel: AI_USER,
      },
      message: '',
      parent_comment,
      // if connection to rtc is down, set the message to error
      status: await RTInterfaceAPI.isConnected() ? TimelineStatus.PENDING : TimelineStatus.ERROR,
    }) as ITimeline

    addMessage(item, conversation_id)
  }

  /**
   * Interface to create a timeline message.
   */
  interface CreateMessageOptions {
    /** Id of the conversation. */
    conversation_id?: string

    /** Message to create. */
    message?: string

    /**
     * Parent's message.
     * @deprecated Use parentMessage instead.
     */
    parent_comment?: ITimeline

    /** Message textContent. */
    textContent?: RTCTextContent[]

    /** Parent's message. */
    parentMessage?: ITimeline

    /** Is an AI answer? */
    isBot?: boolean

    /** Temporary ID of the message. For AI only. */
    temp_message_id?: string
  }

  /**
   * Get mentions in message.
   * @param message - Raw message.
   * @returns Mentions and message with names instead of initials.
   */
  async function getMentions(message: string) {
    let processedMessage = message

    // user mentions
    const loginsMatches = [...message.matchAll(/data-login="(.*?)".*?(?:data-full-name="(.*?)")?.*?>@(.*?)<\/a>/g)]
    const logins = [...new Set(loginsMatches.map(async (match) => {
      if (match.length < 4)
        return
      /**
       * 1: login
       * 2: user name
       * 3: initials
       */
      // replace @... by @full-name
      const rawMention = match[0]
      let userName = match[2]
      if (!userName) {
        const storeUsers = useUsersStore()
        const user = await storeUsers.getUser(match[1])
        if (user)
          userName = user.displayName || `${user.first_name} ${user.last_name}`
        else userName = match[1]
      }
      const displayMention = match[0].replace(`@${match[3]}`, `@${userName}`)
      processedMessage = processedMessage?.replace(rawMention, displayMention)
      return match[1] // login
    }))]

    // usergroups mentions
    const usergroupsMatches = [...message.matchAll(/data-users-group-uri="(?:&lt;|<)(.*)(?:&gt;|>)".*?@(.*?)<\/a>/g)]
    const usergroups = [...new Set(usergroupsMatches.map((match) => {
      if (match.length < 3)
        return
      /**
       * 1: usergroup uuid
       * 2: usergroup name
       */
      return { uuid: match[1], name: match[2] }
    }))]

    const mentions = {
      logins: await Promise.all(logins),
      usergroups,
      ...message.match(/data-audience\s*=\s*['"][^'"]*['"]/gi) ? { all: true } : {},
    }
    return { mentions, processedMessage }
  }

  /**
   * Create the timeline message.
   * @param options - Options.
   * @param options.message - Message to create.
   * @param options.parentMessage - Parent's message.
   * @param options.parent_comment - Deprecated: Use option.parentMessage instead.
   * @param options.isBot - Is an AI answer?
   * @param options.temp_message_id - Temporary ID of the message. For AI only.
   * @param options.conversation_id - Id of the conversation.
   * @param options.textContent use to store additional information of a message (currently used for Aura rephrasing queries)
   */
  // eslint-disable-next-line complexity
  async function createMessage({
    message,
    parent_comment,
    parentMessage,
    temp_message_id,
    textContent,
    isBot,
    conversation_id,
  }: CreateMessageOptions): Promise<ITimeline> {
    parentMessage = parentMessage ?? parent_comment

    let rawMessage = message

    if (isBot && temp_message_id) {
      const aiAnswer = messagesMap.get(temp_message_id)
      rawMessage = (aiAnswer?.message_details as IMessageDetails).content
    }

    if (!rawMessage)
      throw new Error('Empty message')

    const parentMessageId = parentMessage?.id ? Number(parentMessage.id) : undefined

    let mentions = {}
    let processedMessage = rawMessage
    if (!isBot && rawMessage)
      ({ mentions, processedMessage } = await getMentions(rawMessage))

    // notifications only want the raw message
    processedMessage = unescapeHTML(processedMessage)

    /**
     * stringified textContent data as to be store by RTC backend it data should be a string
     */
    const formattedTextContent = textContent?.map(el => ({ ...el, data: JSON.stringify(el.data) })) || []

    const TxID = await RTConv.newMessage({
      convId: conversation_id
        ? Number.parseInt(conversation_id)
        : Number.parseInt(currentTimelineId.value),
      contents: [{ type: 'text', data: rawMessage, notifData: processedMessage }, ...formattedTextContent],
      mentions: !isBot ? mentions : undefined,
      parentMessageId,
      isBot,
      waitForResponse: false,
    })

    if (!TxID)
      throw new Error('No TxID')

    if (isBot && temp_message_id) {
      const newMessage = messagesMap.get(temp_message_id)

      if (!newMessage)
        throw new Error('Empty new message')

      newMessage.message_uri = TxID
      return newMessage
    }
    else {
      const item = await getTemporaryTimelineMessage({
        external_app_id: 'RTC',
        currentTimelineId: conversation_id ?? currentTimelineId.value,
        message_uri: TxID,
        author: storeUsers.currentUser as IUser,
        message: rawMessage,
        parent_comment: parentMessage,
        // if connection to rtc is down, set the message to error
        status: await RTInterfaceAPI.isConnected() ? TimelineStatus.PENDING : TimelineStatus.ERROR,
      }) as ITimeline

      if (!isBot)
        addMessage(item, conversation_id ?? currentTimelineId.value)
        // Tmp message status will be set to error at newMessage event if error.

      return item
    }
  }

  const createInternalMessage = async (action: string, data: any, thread_id: string) => {
    const convId = Number(thread_id.split(':')
      ?.pop() || thread_id)
    RTConv.newMessage({
      convId,
      contents: [{
        type: 'internal',
        data: {
          eventType: action as RTCInternalEventType,
          eventData: data,
        },
      }],
      waitForResponse: false,
    })
  }

  const forwardMessage = async (subject_uri: string, message: ITimeline) => {
    const convId = Number(subject_uri.split(':')
      ?.pop() || subject_uri)

    let mentions = {}
    if (isMessageDetails(message.message_details))
      ({ mentions } = await getMentions(message.message_details.content))

    if (storeContents.isActiveConversationFromV2) {
      // forward RTConv -> RTConv
      return RTConv.forwardMessage({
        convId,
        msgId: Number(message.id),
        msgConvId: Number(currentTimelineId.value),
        forwardedFrom: message.author.login,
        mentions,
        waitForResponse: true,
      })
    }
    else {
      // forward swym -> RTConv
      return RTConv.forwardMessage({
        convId,
        msgId: message.subject_uri,
        msgConvId: currentTimelineId.value,
        forwardedFrom: message.author.login,
        mentions,
        waitForResponse: true,
      })
    }
  }

  const updateComment = (comment: IComment) => {
    const messageUri = comments_id.get(comment.subject_uri)
    const message = messageUri && messagesMap.get(messageUri)

    if (message) {
      const messageDetails = message.message_details as IMessageDetailsInternalEvent
      messageDetails.model_content = comment.description
      messageDetails.modification_date = comment.modified_at
    }
  }
  // #endregion

  return {
    // #region COMPUTED
    currentTimelineList,
    thread_selected: computed(() => storeContents.thread_selected),
    // #endregion

    // #region METHODS
    getMessageById,
    deleteMessage,
    editMessageText,
    // #endregion

    // #region ACTIONS
    fetchTimeline,
    refreshTimeline,
    fetchNextPage,
    fetchTimelineAroundMessage,
    mergeMessage,
    addMessage,
    addTemporaryAIMessage,
    removeMessage,
    editMessage,
    createMessage,
    getComments: storeComments.getComments,
    getCommentsStats: storeComments.getComments,
    addMyEndorse: storeComments.addMyEndorse,
    removeMyEndorse: storeComments.removeMyEndorse,
    getUsersWhoEndorse: storeComments.getUsersWhoEndorse,
    createInternalMessage,
    forwardMessage,
    updateComment,
    // #endregion
  }
})
